"use client";
import Link from "next/link";
import Image from "next/image";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left";
import { ThemeToggle } from "@/components/ThemeToggle";

const logo = "/logo.webp";

interface AuthLayoutProps {
  children: React.ReactNode;
  heading: string;
  subheading: string;
}

/**
 * Centered, minimal auth shell. A single clean column — brand logo, heading,
 * then the form card — floating on a subtle branded backdrop. Used by every
 * auth surface (sign in / sign up / forgot / reset) so they stay consistent.
 */
export default function AuthLayout({ children, heading, subheading }: AuthLayoutProps) {
  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden">
      {/* ── Background built specifically for the auth screen (no shared component) ── */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* base canvas */}
        <div className="absolute inset-0 bg-background" />
        {/* faint blueprint grid, radially masked so it never hard-cuts at the edges */}
        <div className="absolute inset-0 opacity-50 [background-image:linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] [background-size:46px_46px] [mask-image:radial-gradient(115%_80%_at_50%_34%,#000_28%,transparent_72%)]" />
        {/* primary glow pooled directly behind the centered card */}
        <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.13] blur-[140px]" />
        {/* warm accent wash, low on the page */}
        <div className="absolute -bottom-28 left-1/2 h-[420px] w-[780px] -translate-x-1/2 rounded-full bg-accent/[0.06] blur-[150px]" />
        {/* corner vignette to settle the glows into the canvas */}
        <div className="absolute inset-0 [background:radial-gradient(125%_125%_at_50%_50%,transparent_55%,hsl(var(--background))_100%)]" />
      </div>

      {/* ── Slim top bar ── */}
      <header className="relative z-10 flex items-center justify-between px-4 py-4 sm:px-6">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Home</span>
          <span className="sr-only sm:hidden">Back to home</span>
        </Link>
        <ThemeToggle />
      </header>

      {/* ── Centered form column ── */}
      <main
        id="main-content"
        tabIndex={-1}
        aria-label={heading}
        className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-8 sm:px-6"
      >
        <div className="w-full max-w-[400px]">
          {/* Brand + heading */}
          <div className="mb-6 flex flex-col items-center text-center animate-fade-up">
            <Link href="/" className="mb-5 inline-flex items-center gap-2.5">
              <Image
                src={logo}
                alt=""
                width={36}
                height={36}
                className="h-9 w-9 rounded-xl object-cover"
              />
              <span className="font-display text-lg font-bold tracking-tight text-foreground">
                UEFN<span className="opacity-70">Marketplace</span>
              </span>
            </Link>
            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
              {heading}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{subheading}</p>
          </div>

          {children}
        </div>
      </main>
    </div>
  );
}
